import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@quebon/ui/components/dialog'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { commerceApi, ProductResponse } from '@/api/commerce'
import { tenantApi, License, OrganizationSubscription } from '@/api/tenant'
import { contentApi } from '@/api/content'
import { lcClassApi, LcClass } from '@/api/b2b'
import { CheckCircle2, ShoppingCart, Package, Calendar as CalendarIcon, AlertCircle, BookOpen, Users, GraduationCap, Clock, Target, FileText, Eye, Tag, ListTree } from 'lucide-react'
import { clsx } from 'clsx'
import CurriculumTreeViewer from '@/components/common/CurriculumTreeViewer'
import { toast } from 'sonner'
import { getApiErrorMessage } from '@/utils/apiError'
import { useAuthStore } from '@/store/authStore'

function addMonths(date: Date, months: number): Date {
  const d = new Date(date)
  d.setMonth(d.getMonth() + months)
  return d
}

function formatLocalDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export default function ProductManagement() {
  const { t } = useTranslation('admin')
  const navigate = useNavigate()
  const [licenses, setLicenses] = useState<License[]>([])  // {t('product.available.title')} (Open to Org)
  const [subscriptions, setSubscriptions] = useState<OrganizationSubscription[]>([])  // {t('product.purchased.title')}
  const [products, setProducts] = useState<ProductResponse[]>([])
  const [classes, setClasses] = useState<LcClass[]>([])  // 클래스 목록
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [purchasingProductId, setPurchasingProductId] = useState<number | null>(null)
  const [selectedProduct, setSelectedProduct] = useState<ProductResponse | null>(null)  // 구독하기 모달용
  const [detailProduct, setDetailProduct] = useState<ProductResponse | null>(null)  // {t('product.available.viewDetail')} 모달용
  const [selectedPricingId, setSelectedPricingId] = useState<number | null>(null)
  const [selectedSeats, setSelectedSeats] = useState<number | 'unlimited'>('unlimited')
  const [startDate, setStartDate] = useState<string>(() => {
    const today = new Date()
    return today.toISOString().split('T')[0]
  })
  const [endDate, setEndDate] = useState<string>('') // legacy - 자동 계산으로 대체됨
  const user = useAuthStore((state) => state.user)
  
  // 커리큘럼 구조보기 모달
  const [isTreeViewerOpen, setIsTreeViewerOpen] = useState(false)
  const [selectedCurriculumId, setSelectedCurriculumId] = useState<number | null>(null)
  const [selectedCurriculumTitle, setSelectedCurriculumTitle] = useState<string>('')
  
  // {t('product.available.viewDetail')} 모달 탭
  const [detailTab, setDetailTab] = useState<'contents' | 'pricing'>('contents')

  const seatOptions: (number | 'unlimited')[] = ['unlimited', 50, 100, 200, 500, 1000, 2000, 5000, 10000]
  
  // 커리큘럼 계층 구조 조회
  const { data: hierarchyData, isLoading: isHierarchyLoading } = useQuery({
    queryKey: ['curriculum-hierarchy', selectedCurriculumId],
    queryFn: () => contentApi.getContentHierarchy(selectedCurriculumId!),
    enabled: isTreeViewerOpen && !!selectedCurriculumId,
  })
  
  // 커리큘럼 구조보기 핸들러
  const handleViewCurriculumStructure = (curriculumId: number, title: string) => {
    setSelectedCurriculumId(curriculumId)
    setSelectedCurriculumTitle(title)
    setIsTreeViewerOpen(true)
  }
  
  const handleCloseTreeViewer = () => {
    setIsTreeViewerOpen(false)
    setSelectedCurriculumId(null)
    setSelectedCurriculumTitle('')
  }

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // {t('product.available.title')}(라이선스) 목록 조회 - Open to Org로 공개된 것
      console.log('[ProductManagement] Loading licenses (available products)...')
      const licensesResponse = await tenantApi.getLicenses()
      if (licensesResponse.data.success) {
        const licenses = licensesResponse.data.data || []
        console.log('[ProductManagement] Loaded licenses:', licenses.length)
        setLicenses(licenses)
      } else {
        console.warn('[ProductManagement] licenses API returned success=false')
      }
      
      // {t('product.purchased.title')}(구독) 목록 조회
      console.log('[ProductManagement] Loading subscriptions (purchased products)...')
      const subscriptionsResponse = await tenantApi.getSubscriptions()
      if (subscriptionsResponse.data.success) {
        const subs = subscriptionsResponse.data.data || []
        console.log('[ProductManagement] Loaded subscriptions:', subs.length)
        setSubscriptions(subs)
        
        // 구독과 라이선스 모두 0개면 경고 로그 (TenantContext 문제 가능성)
        if (subs.length === 0 && (licensesResponse.data.data || []).length === 0) {
          console.warn('[ProductManagement] ⚠️ 구독과 라이선스 모두 0개입니다. ' +
            'TenantContext가 설정되지 않았을 수 있습니다. ' +
            'X-Tenant-Id 헤더와 TenantFilter 설정을 확인하세요.')
        }
      } else {
        console.warn('[ProductManagement] subscriptions API returned success=false')
      }

      // 전체 상품 목록 조회 (가격 정보 등을 위해)
      const productsData = await commerceApi.getProducts()
      console.log('[ProductManagement] Loaded products:', productsData.length)
      setProducts(productsData)
      
      // 클래스 목록 조회
      try {
        const classesResponse = await lcClassApi.getAll()
        if (classesResponse.data.success) {
          const classesData = classesResponse.data.data || []
          console.log('[ProductManagement] Loaded classes:', classesData.length)
          setClasses(classesData)
        }
      } catch (classErr) {
        console.warn('[ProductManagement] Failed to load classes:', classErr)
      }
    } catch (err: any) {
      console.error('[ProductManagement] Failed to load data:', err)
      setError(err.message || t('product.toast.loadError'))
    } finally {
      setLoading(false)
    }
  }

  // {t('product.available.viewDetail')} 모달 열기 (탭 지정 가능)
  const handleShowDetail = async (product: ProductResponse, tab: 'contents' | 'pricing' = 'contents') => {
    setDetailTab(tab)
    
    // Pricing 탭으로 열 때 구매 관련 상태 초기화
    if (tab === 'pricing') {
      setSelectedSeats('unlimited')
      const today = new Date()
      setStartDate(today.toISOString().split('T')[0])
    }
    
    try {
      const productWithDetails = await commerceApi.getProduct(product.id)
      console.log('[ProductManagement] Product detail loaded:', {
        productId: productWithDetails.id,
        contents: productWithDetails.contents,
        contentsLength: productWithDetails.contents?.length || 0
      })
      setDetailProduct(productWithDetails)
      
      // 기본 {t('product.purchaseModal.pricingOptions')} 선택
      if (tab === 'pricing') {
        const defaultPricing = productWithDetails.pricingOptions?.find(p => p.isDefault) || productWithDetails.pricingOptions?.[0]
        setSelectedPricingId(defaultPricing?.id || null)
      }
    } catch (err) {
      console.error('[ProductManagement] Failed to load product details:', err)
      setDetailProduct(product)
      
      if (tab === 'pricing') {
        const defaultPricing = product.pricingOptions?.find(p => p.isDefault) || product.pricingOptions?.[0]
        setSelectedPricingId(defaultPricing?.id || null)
      }
    }
  }

  const handleCloseDetailModal = () => {
    setDetailProduct(null)
    setDetailTab('contents')
    setSelectedPricingId(null)
    setSelectedSeats('unlimited')
  }

  // 구독하기 모달 열기 ({t('product.purchaseModal.pricingOptions')} 및 구매 UI)
  const handleViewDetails = async (product: ProductResponse) => {
    // 기본값 초기화
    setSelectedSeats('unlimited')
    const today = new Date()
    setStartDate(today.toISOString().split('T')[0])
    
    // 개별 상품 API를 호출하여 가격 정보를 확실히 가져옴
    // (목록 API에서 pricingOptions가 누락될 수 있음)
    try {
      const productWithPricing = await commerceApi.getProduct(product.id)
      setSelectedProduct(productWithPricing)
      
      // 기본 {t('product.purchaseModal.pricingOptions')} 선택
      const defaultPricing = productWithPricing.pricingOptions?.find(p => p.isDefault) || productWithPricing.pricingOptions?.[0]
      setSelectedPricingId(defaultPricing?.id || null)
      
      console.log('[ProductManagement] Product for subscription loaded:', {
        productId: productWithPricing.id,
        pricingOptions: productWithPricing.pricingOptions?.length || 0,
        defaultPricingId: defaultPricing?.id
      })
    } catch (err) {
      console.error('[ProductManagement] Failed to load product details:', err)
      // 폴백: 기존 product 사용
      setSelectedProduct(product)
      const defaultPricing = product.pricingOptions?.find(p => p.isDefault) || product.pricingOptions?.[0]
      setSelectedPricingId(defaultPricing?.id || null)
    }
  }

  const handleCloseModal = () => {
    setSelectedProduct(null)
    setSelectedPricingId(null)
    setSelectedSeats('unlimited')
    const today = new Date()
    setStartDate(today.toISOString().split('T')[0])
  }

  const handlePurchase = async (purchaseProduct?: ProductResponse | null) => {
    // detailProduct 또는 selectedProduct에서 구매 처리
    const targetProduct = purchaseProduct || selectedProduct
    
    if (!targetProduct || !selectedPricingId) {
      toast.warning(t('product.toast.selectPricing'))
      return
    }

    if (!startDate) {
      toast.warning(t('product.toast.selectStartDate'))
      return
    }

    const selectedPricing = targetProduct.pricingOptions?.find(p => p.id === selectedPricingId)
    if (!selectedPricing) {
      toast.error(t('product.toast.pricingNotFound'))
      return
    }

    const seatsText = selectedPricing.maxSeats === 0 ? t('product.purchased.unlimited') : t('product.purchased.seatCount', { count: selectedPricing.maxSeats })
    const validityText = selectedPricing.pricingType === 'subscription'
      ? t('product.detailModal.subscription')
      : selectedPricing.billingPeriodMonths
        ? t('product.billingPeriod.months', { count: selectedPricing.billingPeriodMonths })
        : selectedPricing.billingPeriod === 'monthly' ? t('product.billingPeriod.monthlyFull')
        : selectedPricing.billingPeriod === 'quarterly' ? t('product.billingPeriod.quarterlyFull')
        : selectedPricing.billingPeriod === 'yearly' ? t('product.billingPeriod.yearlyFull')
        : selectedPricing.billingPeriod === 'lifetime' ? t('product.billingPeriod.lifetime')
        : '-'
    const startDateText = new Date(startDate).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })

    if (!confirm(t('product.confirmPurchase', {
      name: targetProduct.name,
      price: formatPrice(selectedPricing.price, selectedPricing.currency),
      seats: seatsText,
      validity: validityText,
      startDate: startDateText
    }))) {
      return
    }

    try {
      setPurchasingProductId(targetProduct.id)
      
      console.log('[ProductManagement] Creating order...', {
        productId: targetProduct.id,
        pricingId: selectedPricingId,
        startDate,
      })
      
      // 주문 생성 (seats를 보내지 않으면 백엔드가 pricing.maxSeats 사용)
      const order = await commerceApi.createOrder({
        productId: targetProduct.id,
        pricingId: selectedPricingId,
        quantity: 1,
      })
      console.log('[ProductManagement] Order created:', order)

      // 결제 처리 (Mock 결제)
      console.log('[ProductManagement] Processing payment...', {
        orderId: order.id,
        validFrom: startDate,
      })
      const payment = await commerceApi.processPayment({
        orderId: order.id,
        paymentMethod: 'credit_card',
        cardNumber: '4242424242424242', // Mock 카드 번호
        validFrom: startDate, // 선택한 시작일 전달
      })
      console.log('[ProductManagement] Payment completed:', payment)

      toast.success(t('product.toast.purchaseSuccess', { orderNumber: order.orderNumber }))

      // 모달 닫기 (열려있는 모달에 따라)
      if (detailProduct) handleCloseDetailModal()
      if (selectedProduct) handleCloseModal()

      // 데이터 새로고침 (약간의 지연을 두어 백엔드 처리 완료 대기)
      console.log('[ProductManagement] Reloading data after purchase...')
      await new Promise(resolve => setTimeout(resolve, 1000)) // 1초로 증가
      await loadData()
    } catch (err: any) {
      console.error('[ProductManagement] Purchase failed:', err)
      console.error('[ProductManagement] Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      })
      toast.error(getApiErrorMessage(err, t))
    } finally {
      setPurchasingProductId(null)
    }
  }

  const formatPrice = (price: number, currency: string = 'KRW') => {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: currency,
    }).format(price)
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const isProductPurchased = (productId: number) => {
    // 구독 목록에서 해당 상품이 있는지 확인
    return subscriptions.some(sub => sub.productId === productId && sub.status === 'active')
  }
  
  // 라이선스(구매 가능)에 있지만 아직 구매하지 않은 상품만 필터링
  const getAvailableProducts = () => {
    const purchasedProductIds = subscriptions.map(s => s.productId)
    return licenses.filter(license => !purchasedProductIds.includes(license.productId))
  }
  
  // 특정 라이선스를 사용하는 클래스 목록 조회
  // 참고: subscription.id는 실제로 organization_licenses.license_id 입니다.
  // 백엔드 getOrganizationSubscriptions()가 license.id를 subscription.id로 반환합니다.
  // class_products 테이블의 license_id와 매칭됩니다.
  const getClassesUsingLicense = (licenseId: number) => {
    return classes.filter(cls => 
      cls.licenses?.some(lic => lic.licenseId === licenseId)
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    )
  }

  // {t('product.purchased.title')} = 활성 구독 목록
  const purchasedProducts = subscriptions.filter(sub => sub.status === 'active')
  console.log('[ProductManagement] Purchased products (subscriptions):', purchasedProducts.length)
  
  // {t('product.available.title')} = 라이선스에 있지만 아직 구매하지 않은 것
  const availableLicenses = getAvailableProducts()
  console.log('[ProductManagement] Available licenses:', availableLicenses.length)
  
  // 가격 정보가 있는 상품 목록 ({t('product.available.title')} 표시용)
  const availableProducts = availableLicenses.map(license => {
    const productInfo = products.find(p => p.id === license.productId)
    return { license, productInfo }
  }).filter(item => item.productInfo)

  return (
    <div className="space-y-8">
      {/* 페이지 헤더 */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{t('product.title')}</h1>
        <p className="mt-2 text-gray-600">{t('product.description')}</p>
      </div>

      {/* {t('product.purchased.title')} 섹션 */}
      <section>
        <div className="flex items-center mb-4">
          <Package className="w-5 h-5 text-primary-600 mr-2" />
          <h2 className="text-xl font-semibold text-gray-900">{t('product.purchased.title')}</h2>
          <span className="ml-2 px-2 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
            {t('product.purchased.count', { count: purchasedProducts.length })}
          </span>
        </div>

        {purchasedProducts.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
            <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">{t('product.purchased.empty')}</p>
            <p className="text-sm text-gray-500 mt-2">{t('product.purchased.emptyHint')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {purchasedProducts.map((subscription) => {
              return (
                <div
                  key={subscription.id}
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {subscription.productName || t('productDetail.productNumber', { id: subscription.productId })}
                        </h3>
                      </div>
                      <div className="flex items-center mt-2">
                        <CheckCircle2 className="w-4 h-4 mr-1 text-green-600" />
                        <span className="text-sm font-medium text-green-600">
                          {t('product.purchased.subscribing')}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm text-gray-600">
                    {/* 포함된 커리큘럼 (번들/단일 동일 형식) */}
                    {(subscription.isBundle && subscription.bundleCurriculumTitles && subscription.bundleCurriculumTitles.length > 0) || subscription.curriculumTitle ? (
                      <div className="flex items-start">
                        <BookOpen className="w-4 h-4 mr-2 text-purple-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="text-xs text-gray-500 mb-1 flex items-center gap-1.5">
                            {t('product.purchased.curricula')}
                            {subscription.isBundle && (
                              <span className="px-1.5 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-700 rounded-full">
                                {t('product.purchased.bundle')}
                              </span>
                            )}
                          </span>
                          <div className="space-y-1">
                            {subscription.isBundle && subscription.bundleCurriculumTitles && subscription.bundleCurriculumTitles.length > 0 ? (
                              subscription.bundleCurriculumTitles.map((title, idx) => {
                                // 번들의 경우 products에서 해당 product를 찾아 contentId 매칭
                                const product = products.find(p => p.id === subscription.productId)
                                const content = product?.contents?.find(c => c.title === title)
                                const contentId = content?.contentId
                                return (
                                  <div key={idx} className="flex items-center gap-1.5 text-xs text-purple-700">
                                    <span>{title}</span>
                                    {contentId && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleViewCurriculumStructure(contentId, title)
                                        }}
                                        className="p-0.5 hover:bg-purple-100 rounded transition-colors"
                                        title={t('product.purchased.viewStructure')}
                                      >
                                        <ListTree className="w-3.5 h-3.5 text-purple-500 hover:text-purple-700" />
                                      </button>
                                    )}
                                  </div>
                                )
                              })
                            ) : subscription.curriculumTitle && (() => {
                              // 단일 커리큘럼: subscription 또는 products에서 contentId 가져오기
                              const product = products.find(p => p.id === subscription.productId)
                              const contentId = subscription.curriculumContentId || product?.linkedContentId || product?.contents?.[0]?.contentId
                              return (
                                <div className="flex items-center gap-1.5 text-xs text-purple-700">
                                  <span>{subscription.curriculumTitle}</span>
                                  {contentId && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleViewCurriculumStructure(contentId, subscription.curriculumTitle!)
                                      }}
                                      className="p-0.5 hover:bg-purple-100 rounded transition-colors"
                                      title={t('product.purchased.viewStructure')}
                                    >
                                      <ListTree className="w-3.5 h-3.5 text-purple-500 hover:text-purple-700" />
                                    </button>
                                  )}
                                </div>
                              )
                            })()}
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {/* 좌석 사용 현황 - 클래스를 통한 사용자 수만 표시 */}
                    {(() => {
                      const usingClasses = getClassesUsingLicense(subscription.id)
                      // 클래스 연결이 있을 때만 클래스 내 학생 수 합계, 없으면 0
                      const actualUsedSeats = usingClasses.length > 0
                        ? usingClasses.reduce((sum, cls) => sum + (cls.currentStudents ?? 0), 0)
                        : 0
                      return (
                        <div className="flex items-center">
                          <Users className="w-4 h-4 mr-2 text-gray-400" />
                          <span>
                            {t('product.purchased.usedSeats', { count: actualUsedSeats })}
                            {' ('}
                            {t('product.purchased.purchasedSeats')}
                            <span className="font-medium text-gray-900">
                              {subscription.purchasedSeats === 0 ? t('product.purchased.unlimited') : t('product.purchased.seatCount', { count: subscription.purchasedSeats })}
                            </span>
                            {')'}
                          </span>
                        </div>
                      )
                    })()}

                    {/* 유효기간 */}
                    <div className="flex items-center">
                      <CalendarIcon className="w-4 h-4 mr-2 text-gray-400" />
                      <span>{t('product.purchased.startDate', { date: formatDate(subscription.validFrom) })}</span>
                    </div>
                    {subscription.validUntil && (
                      <div className="flex items-center">
                        <CalendarIcon className="w-4 h-4 mr-2 text-gray-400" />
                        <span>{t('product.purchased.expiryDate', { date: formatDate(subscription.validUntil) })}</span>
                      </div>
                    )}
                    {!subscription.validUntil && (
                      <div className="flex items-center text-green-600">
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        <span>{t('product.purchased.noExpiry')}</span>
                      </div>
                    )}
                  </div>

                  {/* 이 상품을 이용한 클래스들 */}
                  {(() => {
                    const usingClasses = getClassesUsingLicense(subscription.id)
                    return (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-center text-xs text-gray-500 mb-2">
                          <GraduationCap className="w-3.5 h-3.5 mr-1" />
                          <span>{t('product.purchased.usingClasses', { count: usingClasses.length })}</span>
                        </div>
                        {usingClasses.length === 0 ? (
                          <div className="space-y-2">
                            <span className="text-xs text-gray-400 block">{t('product.purchased.noClasses')}</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate('/admin/classes', { 
                                  state: { 
                                    openCreate: true, 
                                    licenseId: subscription.id,
                                    productName: subscription.productName
                                  } 
                                })
                              }}
                              className="text-xs px-2 py-1 bg-primary-50 text-primary-600 rounded hover:bg-primary-100 transition-colors"
                            >
                              {t('product.purchased.createClass')}
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="flex flex-wrap gap-1.5">
                              {usingClasses.map(cls => (
                                <button
                                  key={cls.id}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    navigate('/admin/classes', { state: { selectedClassId: cls.id } })
                                  }}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-primary-50 text-primary-700 rounded hover:bg-primary-100 transition-colors cursor-pointer"
                                >
                                  <GraduationCap className="w-3 h-3" />
                                  {cls.name}
                                </button>
                              ))}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate('/admin/classes', { 
                                  state: { 
                                    openCreate: true, 
                                    licenseId: subscription.id,
                                    productName: subscription.productName
                                  } 
                                })
                              }}
                              className="text-xs px-2 py-1 bg-primary-50 text-primary-600 rounded hover:bg-primary-100 transition-colors"
                            >
                              {t('product.purchased.createMoreClass')}
                            </button>
                          </div>
                        )}
                      </div>
                    )
                  })()}

                  {/* License 인원추가/연장 버튼 */}
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        toast.info(t('product.purchased.contactSales'))
                      }}
                      className="w-full text-xs text-blue-500 hover:text-blue-700 hover:underline transition-colors text-center py-1"
                    >
                      {t('product.purchased.licenseExtend')}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </section>

      {/* {t('product.available.title')} 섹션 - 상품이 있을 때만 표시 */}
      {availableProducts.length > 0 && (
      <section>
        <div className="flex items-center mb-4">
          <ShoppingCart className="w-5 h-5 text-primary-600 mr-2" />
          <h2 className="text-xl font-semibold text-gray-900">{t('product.available.title')}</h2>
          <span className="ml-2 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
            {t('product.available.count', { count: availableProducts.length })}
          </span>
        </div>

        {(
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {availableProducts.map(({ license, productInfo }) => {
              if (!productInfo) return null
              const product = productInfo
              const defaultPricing = product.pricingOptions?.find(p => p.isDefault) || product.pricingOptions?.[0]
              const isBundle = product.isBundle || (product.contents && product.contents.length > 1)
              const curriculumContents = product.contents && product.contents.length > 0 
                ? product.contents 
                : (license.curriculumContentId ? [{ contentId: license.curriculumContentId, title: license.curriculumTitle || '', customTitle: null, sequenceOrder: 0, shortDescription: null, learningObjectives: null, lessonCount: 0, estimatedHours: 0 }] : [])

              return (
                <div
                  key={license.id}
                  className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
                >
                  {product.thumbnailUrl && (
                    <div className="h-48 bg-gray-100 overflow-hidden">
                      <img
                        src={product.thumbnailUrl}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  <div className="p-6">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {product.name}
                          </h3>
                          {isBundle && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
                              {t('product.purchased.bundle')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {product.shortDescription && (
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                        {product.shortDescription}
                      </p>
                    )}

                    {/* 포함된 커리큘럼 정보 */}
                    {curriculumContents.length > 0 && (
                      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center text-xs text-gray-500 mb-2">
                          <BookOpen className="w-3.5 h-3.5 mr-1" />
                          <span>{t('product.available.curricula', { count: curriculumContents.length })}</span>
                        </div>
                        <div className="space-y-1">
                          {curriculumContents.slice(0, 3).map((content, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-1 truncate flex-1">
                                <span className="text-gray-700 truncate">{content.title}</span>
                                {content.contentId && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleViewCurriculumStructure(content.contentId, content.title)
                                    }}
                                    className="p-0.5 hover:bg-purple-100 rounded transition-colors flex-shrink-0"
                                    title={t('product.purchased.viewStructure')}
                                  >
                                    <ListTree className="w-3.5 h-3.5 text-purple-500 hover:text-purple-700" />
                                  </button>
                                )}
                              </div>
                              {content.lessonCount > 0 && (
                                <span className="text-xs text-gray-500 ml-2 whitespace-nowrap">
                                  {t('product.available.lessonCount', { count: content.lessonCount })}
                                </span>
                              )}
                            </div>
                          ))}
                          {curriculumContents.length > 3 && (
                            <span className="text-xs text-gray-500">{t('product.available.moreCount', { count: curriculumContents.length - 3 })}</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* 가격 정보 */}
                    {defaultPricing && (
                      <div className="mb-4">
                        {defaultPricing.isNegotiable ? (
                          <span className="text-xl font-bold text-gray-700">
                            {t('product.available.negotiatePrice')}
                          </span>
                        ) : (
                          <>
                            <div className="flex items-baseline">
                              {defaultPricing.originalPrice && defaultPricing.originalPrice > defaultPricing.price && (
                                <span className="text-sm text-gray-400 line-through mr-2">
                                  {formatPrice(defaultPricing.originalPrice, defaultPricing.currency)}
                                </span>
                              )}
                              <span className="text-xl font-bold text-primary-600">
                                {formatPrice(defaultPricing.price, defaultPricing.currency)}
                              </span>
                              {defaultPricing.billingPeriod && (
                                <span className="text-sm text-gray-500 ml-1">
                                  / {defaultPricing.billingPeriod === 'monthly' ? t('product.billingPeriod.monthly') :
                                      defaultPricing.billingPeriod === 'yearly' ? t('product.billingPeriod.yearly') :
                                      defaultPricing.billingPeriod}
                                </span>
                              )}
                            </div>
                            {defaultPricing.discountPercentage && defaultPricing.discountPercentage > 0 && (
                              <span className="inline-block mt-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded">
                                {defaultPricing.discountPercentage}% {t('product.detailModal.discountLabel')}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {/* 버튼들 */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleShowDetail(product)}
                        className="flex-1 py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center bg-gray-100 text-gray-700 hover:bg-gray-200"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        {t('product.available.viewDetail')}
                      </button>
                      <button
                        onClick={() => handleShowDetail(product, 'pricing')}
                        className="flex-1 py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center bg-primary-600 text-white hover:bg-primary-700"
                      >
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        {t('product.available.purchase')}
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </section>
      )}

      {/* 상품 상세 모달 */}
      <Dialog open={!!selectedProduct} onOpenChange={(open) => { if (!open) handleCloseModal() }}>
      <DialogContent className="sm:max-w-3xl">
        {selectedProduct?.name && <DialogHeader><DialogTitle>{selectedProduct.name}</DialogTitle></DialogHeader>}
        {selectedProduct && (
          <div className="space-y-6">
            {/* 썸네일 */}
            {selectedProduct.thumbnailUrl && (
              <div className="w-full h-64 bg-gray-100 rounded-lg overflow-hidden">
                <img
                  src={selectedProduct.thumbnailUrl}
                  alt={selectedProduct.name}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* 설명 */}
            {selectedProduct.description && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('product.purchaseModal.productDescription')}</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{selectedProduct.description}</p>
              </div>
            )}

            {selectedProduct.shortDescription && !selectedProduct.description && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('product.purchaseModal.productDescription')}</h3>
                <p className="text-gray-700">{selectedProduct.shortDescription}</p>
              </div>
            )}

            {/* 포함된 커리큘럼 요약 (구독 모달에서는 간단히 표시) */}
            {selectedProduct.contents && selectedProduct.contents.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-700 flex items-center">
                    <BookOpen className="w-4 h-4 mr-1 text-primary-600" />
                    {t('product.purchaseModal.curricula')}
                  </h3>
                  <span className="text-xs text-gray-500">{t('product.purchaseModal.curriculaCount', { count: selectedProduct.contents.length })}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedProduct.contents.map((content, idx) => (
                    <span key={idx} className="inline-flex items-center px-2 py-1 text-xs bg-white border border-gray-200 text-gray-700 rounded">
                      {content.title}
                      {content.lessonCount > 0 && (
                        <span className="ml-1 text-gray-400">{t('product.purchaseModal.lessonCount', { count: content.lessonCount })}</span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* 단일 커리큘럼인 경우 */}
            {(!selectedProduct.contents || selectedProduct.contents.length === 0) && selectedProduct.curriculumTitle && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <BookOpen className="w-4 h-4 mr-1 text-primary-600" />
                  {t('product.purchaseModal.linkedCurriculum')}
                </h3>
                <span className="inline-flex items-center px-2 py-1 text-xs bg-white border border-gray-200 text-gray-700 rounded">
                  {selectedProduct.curriculumTitle}
                </span>
              </div>
            )}

            {/* {t('product.purchaseModal.subscriptionSettings')} */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <CalendarIcon className="w-5 h-5 mr-2 text-primary-600" />
                {t('product.purchaseModal.subscriptionSettings')}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('product.purchaseModal.startDate')} <span className="text-red-500">{t('product.purchaseModal.startDateRequired')}</span>
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    {t('product.purchaseModal.startDateNote')}
                  </p>
                </div>
              </div>
            </div>

            {/* {t('product.purchaseModal.pricingOptions')} */}
            {selectedProduct.pricingOptions && selectedProduct.pricingOptions.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('product.purchaseModal.pricingOptions')}</h3>
                <div className="space-y-3">
                  {selectedProduct.pricingOptions.map((pricing) => (
                    <label
                      key={pricing.id}
                      className={clsx(
                        'block p-4 border-2 rounded-lg cursor-pointer transition-all',
                        selectedPricingId === pricing.id
                          ? 'border-primary-600 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <div className="flex items-start">
                        <input
                          type="radio"
                          name="pricing"
                          value={pricing.id}
                          checked={selectedPricingId === pricing.id}
                          onChange={() => setSelectedPricingId(pricing.id)}
                          className="mt-1 mr-3"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-gray-900">{pricing.name}</span>
                            {pricing.isDefault && (
                              <span className="px-2 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded">
                                {t('product.purchaseModal.default')}
                              </span>
                            )}
                          </div>
                          {pricing.isNegotiable ? (
                            <div className="mt-2">
                              <span className="text-2xl font-bold text-gray-700">
                                {t('product.available.negotiatePrice')}
                              </span>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-baseline mt-2">
                                {pricing.originalPrice && pricing.originalPrice > pricing.price && (
                                  <span className="text-sm text-gray-400 line-through mr-2">
                                    {formatPrice(pricing.originalPrice, pricing.currency)}
                                  </span>
                                )}
                                <span className="text-2xl font-bold text-primary-600">
                                  {formatPrice(pricing.price, pricing.currency)}
                                </span>
                                {pricing.billingPeriod && (
                                  <span className="text-sm text-gray-500 ml-2">
                                    / {pricing.billingPeriod === 'monthly' ? t('product.billingPeriod.monthly') :
                                        pricing.billingPeriod === 'yearly' ? t('product.billingPeriod.yearly') :
                                        pricing.billingPeriod === 'quarterly' ? t('product.billingPeriod.quarterly') :
                                        pricing.billingPeriod}
                                  </span>
                                )}
                              </div>
                              {pricing.discountPercentage && pricing.discountPercentage > 0 && (
                                <span className="inline-block mt-2 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded">
                                  {t('product.available.discount', { percent: pricing.discountPercentage })}
                                </span>
                              )}
                            </>
                          )}
                          {pricing.hasTrial && pricing.trialDays && (
                            <div className="mt-2 text-sm text-gray-600">
                              <span className="text-green-600 font-medium">{t('product.detailModal.trialDays', { days: pricing.trialDays })} {t('product.detailModal.freeTrial')}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* 구매하기 버튼 */}
            {!isProductPurchased(selectedProduct.id) && (
              <div className="pt-4 border-t border-gray-200">
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">{t('product.purchaseModal.selectedOption')}</span>
                  </div>
                  <div className="space-y-1 text-sm text-gray-600">
                    {selectedPricingId && (
                      <div className="flex justify-between">
                        <span>{t('product.purchaseModal.pricingOptions')}:</span>
                        <span className="font-medium">
                          {selectedProduct.pricingOptions?.find(p => p.id === selectedPricingId)?.name}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>{t('product.purchaseModal.seats')}</span>
                      <span className="font-medium">
                        {selectedSeats === 'unlimited' ? t('product.purchaseModal.unlimitedSeats') : t('product.purchaseModal.seatCount', { count: Number(selectedSeats) })}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('product.purchaseModal.preDiscountPrice')}</span>
                      <span className="font-medium">
                        {selectedSeats === 'unlimited'
                          ? t('product.purchaseModal.perPerson', { price: formatPrice(300000, 'KRW') })
                          : formatPrice(300000 * selectedSeats, 'KRW')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('product.purchaseModal.startDateLabel')}</span>
                      <span className="font-medium">
                        {startDate ? new Date(startDate).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        }) : t('product.purchaseModal.notSelected')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('product.purchaseModal.endDateLabel')}</span>
                      <span className="font-medium">
                        {(() => {
                          const p = selectedProduct?.pricingOptions?.find(p => p.id === selectedPricingId)
                          if (!p || !startDate) return t('product.purchaseModal.notSelected')
                          const months = p.billingPeriodMonths || 0
                          if (months === 0) return t('product.purchaseModal.unlimited')
                          return addMonths(new Date(`${startDate}T00:00:00`), months).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                        })()}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handlePurchase()}
                  disabled={!selectedPricingId || !startDate || purchasingProductId === selectedProduct.id}
                  className={clsx(
                    'w-full py-3 px-6 rounded-lg font-semibold text-lg transition-colors flex items-center justify-center',
                    !selectedPricingId || !startDate || purchasingProductId === selectedProduct.id
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-primary-600 text-white hover:bg-primary-700'
                  )}
                >
                  {purchasingProductId === selectedProduct.id ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      {t('product.purchaseModal.processing')}
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-5 h-5 mr-2" />
                      {t('product.purchaseModal.purchaseButton')}
                    </>
                  )}
                </button>
              </div>
            )}

            {/* 이미 구매한 경우 */}
            {isProductPurchased(selectedProduct.id) && (
              <div className="pt-4 border-t border-gray-200">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2" />
                  <span className="text-green-800 font-medium">{t('product.purchaseModal.alreadyPurchased')}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
      </Dialog>

      {/* {t('product.available.viewDetail')} 모달 (탭 기반: Contents / Pricing) */}
      <Dialog open={!!detailProduct} onOpenChange={(open) => { if (!open) handleCloseDetailModal() }}>
      <DialogContent className="sm:max-w-3xl">
        {detailProduct?.name && <DialogHeader><DialogTitle>{detailProduct.name}</DialogTitle></DialogHeader>}
        {detailProduct && (
          <div className="space-y-6">
            {/* 수평 탭 */}
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px">
                <button
                  onClick={() => setDetailTab('contents')}
                  className={clsx(
                    'px-5 py-3 text-sm font-medium border-b-2 transition-colors',
                    detailTab === 'contents'
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  )}
                >
                  <BookOpen className="w-4 h-4 inline mr-1.5 -mt-0.5" />
                  Contents
                </button>
                <button
                  onClick={() => setDetailTab('pricing')}
                  className={clsx(
                    'px-5 py-3 text-sm font-medium border-b-2 transition-colors',
                    detailTab === 'pricing'
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  )}
                >
                  <Tag className="w-4 h-4 inline mr-1.5 -mt-0.5" />
                  Pricing
                </button>
              </nav>
            </div>

            {/* ===== Contents 탭 ===== */}
            {detailTab === 'contents' && (
              <div className="space-y-4">
                {detailProduct.contents && detailProduct.contents.length > 0 ? (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-primary-600" />
                      {t('product.detailModal.curricula', { count: detailProduct.contents.length })}
                      {detailProduct.isBundle && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
                          {t('product.detailModal.bundleProduct')}
                        </span>
                      )}
                    </h3>
                    <div className="space-y-4">
                      {detailProduct.contents.map((content, idx) => (
                        <div key={idx} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <h4 className="text-md font-semibold text-gray-900">{content.title}</h4>
                              <button
                                onClick={() => handleViewCurriculumStructure(content.contentId, content.title)}
                                className="p-1 hover:bg-gray-100 rounded transition-colors"
                                title={t('product.purchased.viewStructure')}
                              >
                                <ListTree className="w-4 h-4 text-gray-500 hover:text-primary-600" />
                              </button>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              {content.lessonCount > 0 && (
                                <span className="px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded">
                                  {t('product.detailModal.lessonCount', { count: content.lessonCount })}
                                </span>
                              )}
                              {content.estimatedHours > 0 && (
                                <span className="px-2 py-1 text-xs font-medium bg-green-50 text-green-700 rounded">
                                  {t('product.detailModal.estimatedHours', { hours: content.estimatedHours.toFixed(1) })}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* {t('product.detailModal.learningGoals')} */}
                          {content.learningObjectives && (
                            <div className="mb-3">
                              <div className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                <Target className="w-4 h-4 mr-1 text-amber-500" />
                                {t('product.detailModal.learningGoals')}
                              </div>
                              <p className="text-sm text-gray-600 pl-5 whitespace-pre-wrap">
                                {content.learningObjectives}
                              </p>
                            </div>
                          )}

                          {/* {t('product.detailModal.learningContent')} */}
                          {content.shortDescription && (
                            <div className="mb-3">
                              <div className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                <FileText className="w-4 h-4 mr-1 text-blue-500" />
                                {t('product.detailModal.learningContent')}
                              </div>
                              <p className="text-sm text-gray-600 pl-5">
                                {content.shortDescription}
                              </p>
                            </div>
                          )}

                          {/* {t('product.detailModal.keyConcepts')} */}
                          {content.concepts && (
                            <div className="mb-3">
                              <div className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                <Tag className="w-4 h-4 mr-1 text-purple-500" />
                                {t('product.detailModal.keyConcepts')}
                              </div>
                              <div className="pl-5 flex flex-wrap gap-1.5">
                                {content.concepts.split(',').map((concept, i) => (
                                  <span key={i} className="inline-block px-2 py-0.5 text-xs bg-purple-50 text-purple-700 rounded-full">
                                    {concept.trim()}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* 통계 */}
                          <div className="flex items-center gap-4 pt-3 border-t border-gray-100 text-sm text-gray-500">
                            {content.lessonCount > 0 && (
                              <span className="flex items-center">
                                <BookOpen className="w-4 h-4 mr-1" />
                                {t('product.detailModal.totalLessons', { count: content.lessonCount })}
                              </span>
                            )}
                            {content.estimatedHours > 0 && (
                              <span className="flex items-center">
                                <Clock className="w-4 h-4 mr-1" />
                                {t('product.detailModal.estimatedTime', { hours: content.estimatedHours.toFixed(1) })}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : detailProduct.curriculumTitle ? (
                  /* 단일 커리큘럼 */
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                      <BookOpen className="w-5 h-5 mr-2 text-primary-600" />
                      {t('product.detailModal.linkedCurriculum')}
                    </h3>
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        <p className="text-gray-700 font-medium">{detailProduct.curriculumTitle}</p>
                        {detailProduct.linkedContentId && (
                          <button
                            onClick={() => handleViewCurriculumStructure(detailProduct.linkedContentId!, detailProduct.curriculumTitle!)}
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                            title={t('product.purchased.viewStructure')}
                          >
                            <ListTree className="w-4 h-4 text-gray-500 hover:text-primary-600" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <BookOpen className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>{t('product.detailModal.noCurriculum')}</p>
                  </div>
                )}
              </div>
            )}

            {/* ===== Pricing 탭 ===== */}
            {detailTab === 'pricing' && (
              <div className="space-y-6">
                {detailProduct.pricingOptions && detailProduct.pricingOptions.length > 0 ? (
                  <>
                    {/* {t('product.purchaseModal.pricingOptions')} 선택 (라디오) */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">{t('product.detailModal.selectPricing')}</h4>
                      <div className="space-y-3">
                        {detailProduct.pricingOptions.map((pricing) => (
                          <label
                            key={pricing.id}
                            className={clsx(
                              'block border-2 rounded-lg p-4 cursor-pointer transition-all',
                              selectedPricingId === pricing.id
                                ? 'border-primary-600 bg-primary-50/30 ring-1 ring-primary-200'
                                : 'border-gray-200 hover:border-gray-300'
                            )}
                          >
                            <div className="flex items-start gap-3">
                              <input
                                type="radio"
                                name="detail-pricing"
                                value={pricing.id}
                                checked={selectedPricingId === pricing.id}
                                onChange={() => setSelectedPricingId(pricing.id)}
                                className="mt-1"
                              />
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-gray-900">{pricing.name}</span>
                                    {pricing.isDefault && (
                                      <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-primary-100 text-primary-700 rounded">
                                        {t('product.purchaseModal.default')}
                                      </span>
                                    )}
                                    <span className={clsx(
                                      'px-2 py-0.5 text-xs font-medium rounded-full',
                                      pricing.pricingType === 'one_time'
                                        ? 'bg-blue-50 text-blue-700'
                                        : 'bg-green-50 text-green-700'
                                    )}>
                                      {pricing.pricingType === 'one_time' ? t('product.detailModal.oneTime') : t('product.detailModal.subscription')}
                                    </span>
                                  </div>
                                  {!pricing.isNegotiable ? (
                                    <div className="text-right">
                                      {pricing.originalPrice && pricing.originalPrice > pricing.price && (
                                        <span className="block text-sm text-gray-400 line-through">
                                          {formatPrice(pricing.originalPrice, pricing.currency)}
                                        </span>
                                      )}
                                      <span className="text-xl font-bold text-primary-600">
                                        {formatPrice(pricing.price, pricing.currency)}
                                      </span>
                                      {pricing.billingPeriod && (
                                        <span className="text-sm text-gray-500 ml-1">
                                          / {pricing.billingPeriod === 'monthly' ? t('product.billingPeriod.monthly') :
                                              pricing.billingPeriod === 'quarterly' ? t('product.billingPeriod.quarterly') :
                                              pricing.billingPeriod === 'yearly' ? t('product.billingPeriod.yearly') :
                                              pricing.billingPeriod === 'lifetime' ? t('product.billingPeriod.lifetime') :
                                              pricing.billingPeriod}
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-lg font-bold text-gray-700">{t('product.detailModal.separateNegotiation')}</span>
                                  )}
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                  <div className="bg-white rounded-md p-2 border border-gray-100">
                                    <span className="block text-[10px] text-gray-500 mb-0.5">{t('product.detailModal.billingCycle')}</span>
                                    <span className="font-medium text-gray-900 text-xs">
                                      {pricing.billingPeriodMonths
                                        ? t('product.billingPeriod.months', { count: pricing.billingPeriodMonths })
                                        : pricing.billingPeriod === 'monthly' ? t('product.billingPeriod.monthlyFull')
                                        : pricing.billingPeriod === 'quarterly' ? t('product.billingPeriod.quarterlyFull')
                                        : pricing.billingPeriod === 'yearly' ? t('product.billingPeriod.yearlyFull')
                                        : pricing.billingPeriod === 'lifetime' ? t('product.billingPeriod.lifetime')
                                        : '-'}
                                    </span>
                                  </div>
                                  <div className="bg-white rounded-md p-2 border border-gray-100">
                                    <span className="block text-[10px] text-gray-500 mb-0.5">{t('product.detailModal.maxSeats')}</span>
                                    <span className="font-medium text-gray-900 text-xs">
                                      {pricing.maxSeats === 0 ? t('product.purchased.unlimited') : t('product.purchased.seatCount', { count: pricing.maxSeats })}
                                    </span>
                                  </div>
                                  <div className="bg-white rounded-md p-2 border border-gray-100">
                                    <span className="block text-[10px] text-gray-500 mb-0.5">{t('product.detailModal.freeTrial')}</span>
                                    <span className="font-medium text-gray-900 text-xs">
                                      {pricing.hasTrial && pricing.trialDays
                                        ? <span className="text-green-600">{t('product.detailModal.trialDays', { days: pricing.trialDays })}</span>
                                        : t('product.detailModal.noTrial')}
                                    </span>
                                  </div>
                                  {pricing.discountPercentage && pricing.discountPercentage > 0 ? (
                                    <div className="bg-red-50 rounded-md p-2 border border-red-100">
                                      <span className="block text-[10px] text-red-500 mb-0.5">{t('product.detailModal.discountLabel')}</span>
                                      <span className="font-medium text-red-700 text-xs">
                                        {pricing.discountPercentage}% {t('product.detailModal.discountLabel')}
                                      </span>
                                    </div>
                                  ) : (
                                    <div className="bg-white rounded-md p-2 border border-gray-100">
                                      <span className="block text-[10px] text-gray-500 mb-0.5">{t('product.detailModal.discountLabel')}</span>
                                      <span className="font-medium text-gray-400 text-xs">-</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-5 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('product.detailModal.validPeriod')}
                        </label>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">{t('product.purchaseModal.startDate')}</label>
                            <input
                              type="date"
                              value={startDate}
                              onChange={(e) => setStartDate(e.target.value)}
                              min={new Date().toISOString().split('T')[0]}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">{t('product.detailModal.endDate')}</label>
                            <div className="w-full px-4 py-2 bg-gray-100 border border-gray-200 rounded-lg text-gray-700">
                              {(() => {
                                const p = detailProduct?.pricingOptions?.find(p => p.id === selectedPricingId)
                                if (!p || !startDate) return '-'
                                const months = p.billingPeriodMonths || 0
                                if (months === 0) return t('product.purchaseModal.unlimited')
                                const end = addMonths(new Date(`${startDate}T00:00:00`), months)
                                return end.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 선택 요약 */}
                      {selectedPricingId && (
                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-500">{t('product.purchaseModal.pricingOptions')}</span>
                              <span className="font-medium text-gray-900">
                                {detailProduct.pricingOptions.find(p => p.id === selectedPricingId)?.name}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">{t('product.detailModal.summary.license')}</span>
                              <span className="font-medium text-gray-900">
                                {(() => {
                                  const p = detailProduct.pricingOptions.find(p => p.id === selectedPricingId)
                                  if (!p) return '-'
                                  return p.maxSeats === 0 ? t('product.purchased.unlimited') : t('product.purchased.seatCount', { count: p.maxSeats })
                                })()}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">{t('product.detailModal.summary.paymentAmount')}</span>
                              <span className="font-bold text-primary-600">
                                {(() => {
                                  const p = detailProduct.pricingOptions.find(p => p.id === selectedPricingId)
                                  return p?.isNegotiable ? t('product.detailModal.separateNegotiation') : p ? formatPrice(p.price, p.currency) : '-'
                                })()}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">{t('product.detailModal.summary.startDate')}</span>
                              <span className="font-medium text-gray-900">
                                {startDate ? new Date(startDate).toLocaleDateString(undefined, {
                                  year: 'numeric', month: 'long', day: 'numeric',
                                }) : t('product.purchaseModal.notSelected')}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">{t('product.detailModal.summary.endDate')}</span>
                              <span className="font-medium text-gray-900">
                                {(() => {
                                  const p = detailProduct?.pricingOptions?.find(p => p.id === selectedPricingId)
                                  if (!p || !startDate) return '-'
                                  const months = p.billingPeriodMonths || 0
                                  if (months === 0) return t('product.purchaseModal.unlimited')
                                  return addMonths(new Date(`${startDate}T00:00:00`), months).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                                })()}
                              </span>
                            </div>
                            <div className="flex justify-between pt-2 border-t border-gray-100 mt-2">
                              <span className="text-gray-500">{t('product.detailModal.summary.purchaser')}</span>
                              <span className="font-medium text-gray-900">
                                {user ? `${user.fullName} (${user.email})` : '-'}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* 구매하기 버튼 */}
                    {!isProductPurchased(detailProduct.id) ? (
                      <button
                        onClick={() => handlePurchase(detailProduct)}
                        disabled={!selectedPricingId || !startDate || purchasingProductId === detailProduct.id}
                        className={clsx(
                          'w-full py-3 px-6 rounded-lg font-semibold text-lg transition-colors flex items-center justify-center',
                          !selectedPricingId || !startDate || purchasingProductId === detailProduct.id
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-primary-600 text-white hover:bg-primary-700'
                        )}
                      >
                        {purchasingProductId === detailProduct.id ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            {t('product.purchaseModal.processing')}
                          </>
                        ) : (
                          <>
                            <ShoppingCart className="w-5 h-5 mr-2" />
                            {t('product.purchaseModal.purchaseButton')}
                          </>
                        )}
                      </button>
                    ) : (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
                        <CheckCircle2 className="w-5 h-5 text-green-600 mr-2" />
                        <span className="text-green-800 font-medium">{t('product.purchaseModal.alreadyPurchased')}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Tag className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>{t('product.detailModal.noPricing')}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
      </Dialog>

      {/* 커리큘럼 구조보기 모달 */}
      {hierarchyData && (
        <CurriculumTreeViewer
          data={hierarchyData}
          isOpen={isTreeViewerOpen}
          onClose={handleCloseTreeViewer}
          title={selectedCurriculumTitle}
        />
      )}

    </div>
  )
}











